import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";
import { scrollToId } from "./Header";
import { WHATSAPP_LINK, IMG } from "../../data/site";
import { useLang } from "../../i18n/LanguageContext";

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.35 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Hero = () => {
  const { t } = useLang();
  const HEADLINE = t.hero.title;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const overlayY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative h-[100svh] min-h-[640px] w-full overflow-hidden bg-forest"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={IMG.hero}
          alt="Indonesian vanilla beans drying on racks"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/55 to-ink/25" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-ink/30" />
      </motion.div>

      <motion.div
        style={{ y: overlayY }}
        className="relative z-10 mx-auto flex h-full max-w-[1400px] flex-col justify-center px-5 md:px-10"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.8 }}
          className="overline text-gold mb-5"
        >
          {t.hero.overline}
        </motion.p>

        <h1
          aria-label={t.hero.titleAria}
          className="font-serif font-light text-cream leading-[0.92] text-5xl sm:text-7xl lg:text-8xl max-w-4xl"
        >
          {HEADLINE.map((line, i) => (
            <span key={`${line}-${i}`} className="reveal-mask" aria-hidden="true">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className="block"
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9 }}
          className="mt-8 max-w-md text-cream/80 text-base md:text-lg leading-relaxed"
        >
          {t.hero.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.9 }}
          className="mt-10 flex flex-wrap items-center gap-5"
        >
          <a
            data-testid="hero-quote-button"
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-3 bg-gold hover:bg-golddark text-white overline px-8 py-4 transition-colors duration-300"
          >
            {t.common.getQuote}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
          <button
            data-testid="hero-story-button"
            onClick={() => scrollToId("process")}
            className="group inline-flex items-center gap-3 text-cream overline"
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-full border border-cream/50 transition-colors duration-300 group-hover:border-gold group-hover:text-gold">
              <Play className="h-4 w-4 fill-current" />
            </span>
            {t.hero.watchStory}
          </button>
        </motion.div>
      </motion.div>

      {/* animated scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 flex flex-col items-center gap-2"
      >
        <span className="overline text-[0.6rem] text-cream/60">{t.hero.scroll}</span>
        <span className="h-10 w-[1px] overflow-hidden bg-cream/20">
          <motion.span
            animate={{ y: ["-100%", "100%"] }}
            transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
            className="block h-1/2 w-full bg-gold"
          />
        </span>
      </motion.div>
    </section>
  );
};

export default Hero;
